/* ==========================================================================
   Logo upload gate — all client logic.

   Cacheable asset: NO Liquid in this file. Everything store-specific arrives
   via window.MO_LOGO_UPLOAD, rendered by mo-logo-upload-config.liquid.

   --------------------------------------------------------------------------
   GUIDING RULE: THIS MUST NEVER BREAK AN UPLOAD
   --------------------------------------------------------------------------
   Every entry point is wrapped so that a bug, a missing config or a failed
   network call degrades to "let the shopper upload" rather than blocking a
   sale. The gate is enforced on the SERVER (HMAC signature + settings
   re-check). Anything here is convenience and UX — a shopper who defeats this
   file still gets a 401 from the API.

   --------------------------------------------------------------------------
   WHY AN OVERLAY INSTEAD OF A CLICK HANDLER
   --------------------------------------------------------------------------
   A `click` listener on <input type="file"> can be raced by keyboard
   activation (Enter/Space on a focused input opens the picker without a click
   in some browsers). A transparent button positioned over the field intercepts
   pointer AND keyboard activation deterministically, and — crucially — its own
   activation is a fresh user gesture, so we can programmatically open the real
   picker when the gate allows it.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = window.MO_LOGO_UPLOAD;

  // No config rendered -> this page is not gated. Do nothing at all.
  if (!CFG || !CFG.endpoints) return;

  // Never block a merchant working in the theme editor.
  if (CFG.designMode) return;

  var SETTINGS_KEY = "mo_logo_upload_settings_v1";
  var INTENT_KEY = "mo_logo_upload_intent";
  var SETTINGS_TTL_MS = 5 * 60 * 1000;
  var FILE_INPUT_SELECTOR = "#fileInput"; // see the hard rule in T5 / TASKS.md §0.4

  var settings = null; // null = not resolved yet
  var settingsPromise = null;
  var modal = null;
  var lastFocused = null;

  /* ------------------------------------------------------------ utilities */

  function safe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn("[mo-logo-upload] non-fatal:", err);
        }
      }
    };
  }

  function session(action, key, value) {
    try {
      if (action === "get") return window.sessionStorage.getItem(key);
      if (action === "set") return window.sessionStorage.setItem(key, value);
      if (action === "del") return window.sessionStorage.removeItem(key);
    } catch (err) {
      // Safari private mode throws on sessionStorage. Not a reason to fail.
      return null;
    }
  }

  /* ------------------------------------------------------------- settings */

  function cachedSettings() {
    var raw = session("get", SETTINGS_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.at > SETTINGS_TTL_MS) return null;
      return parsed.value;
    } catch (err) {
      return null;
    }
  }

  function loadSettings() {
    if (settingsPromise) return settingsPromise;

    var cached = cachedSettings();
    if (cached) {
      settings = cached;
      settingsPromise = Promise.resolve(cached);
      return settingsPromise;
    }

    var url =
      CFG.endpoints.settings + "?shop=" + encodeURIComponent(CFG.shop || "");

    settingsPromise = fetch(url, { credentials: "omit" })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (json) {
        if (!json) return null;
        settings = json;
        session("set", SETTINGS_KEY, JSON.stringify({ at: Date.now(), value: json }));
        applyCopy();
        return json;
      })
      .catch(function () {
        // Unreachable settings -> stay null -> gate does not block (fail open).
        return null;
      });

    return settingsPromise;
  }

  /** Should the login gate apply on this page, right now? */
  function loginRequired() {
    // Unknown settings -> do NOT block. The server still enforces.
    if (!settings) return false;

    if (CFG.loginOverride === "on") return true;
    if (CFG.loginOverride === "off") return false;
    return settings.requireLogin === true;
  }

  function blocked() {
    return loginRequired() && !CFG.loggedIn;
  }

  /* ---------------------------------------------------------------- modal */

  function copy(key, fallback) {
    if (settings && settings.copy && settings.copy[key]) return settings.copy[key];
    return fallback;
  }

  var applyCopy = safe(function () {
    if (!settings || !settings.copy) return;
    var nodes = document.querySelectorAll("#mo-logo-upload-gate [data-mo-copy]");
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute("data-mo-copy");
      if (settings.copy[key]) nodes[i].textContent = settings.copy[key];
    }
  });

  function getModal() {
    if (modal) return modal;
    var root = document.getElementById("mo-logo-upload-gate");
    if (!root) return null;
    modal = {
      root: root,
      overlay: root.querySelector("[data-mo-modal]"),
      dialog: root.querySelector("[data-mo-dialog]"),
      signin: root.querySelector("[data-mo-signin]"),
      status: root.querySelector("[data-mo-status]"),
    };
    return modal;
  }

  var openModal = safe(function (message, tone) {
    var m = getModal();
    if (!m || !m.overlay) return;

    applyCopy();
    lastFocused = document.activeElement;

    if (m.status) {
      m.status.textContent = message || "";
      m.status.setAttribute("data-tone", tone || "");
    }

    if (m.signin) {
      // Return the shopper to this exact page after signing in.
      var base = CFG.loginUrl || "/account/login";
      var sep = base.indexOf("?") === -1 ? "?" : "&";
      m.signin.setAttribute(
        "href",
        base + sep + "return_url=" + encodeURIComponent(window.location.pathname + window.location.search)
      );
      // Remember WHY they left, so we can greet them on the way back.
      session("set", INTENT_KEY, "1");
    }

    /* The markup carries `hidden` so the modal stays closed even if the
       stylesheet never loads. Drop it here, or it would remain hidden from
       assistive technology while visibly on screen. */
    m.overlay.removeAttribute("hidden");
    m.overlay.classList.add("is-open");
    document.addEventListener("keydown", onKeydown, true);

    var focusTarget = m.signin || m.dialog;
    if (focusTarget && focusTarget.focus) focusTarget.focus();
  });

  var closeModal = safe(function () {
    var m = getModal();
    if (!m || !m.overlay) return;
    m.overlay.classList.remove("is-open");
    m.overlay.setAttribute("hidden", "");
    document.removeEventListener("keydown", onKeydown, true);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  });

  function focusables(dialog) {
    return dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function onKeydown(evt) {
    var m = getModal();
    if (!m || !m.overlay || !m.overlay.classList.contains("is-open")) return;

    if (evt.key === "Escape") {
      evt.preventDefault();
      closeModal();
      return;
    }

    // Focus trap: keep Tab inside the dialog.
    if (evt.key === "Tab" && m.dialog) {
      var items = focusables(m.dialog);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }
  }

  /* -------------------------------------------------------------- overlay */

  var attach = safe(function (input) {
    if (!input || input.getAttribute("data-mo-gated") === "1") return;
    input.setAttribute("data-mo-gated", "1");

    var guard = null;

    /* ------------------------------------------------------------------
       THE OVERLAY IS BUILT LAZILY — only once the gate would really stop
       someone.

       Both features ship OFF, and most shoppers are signed in, so on the
       overwhelming majority of page views nothing here runs. That matters:
       injecting a wrapper div and a button unconditionally would change the
       DOM of every product page for a feature that is switched off, and would
       put a second tab stop over the field for keyboard and screen-reader
       users who are not being gated at all.

       Deferred, the page stays byte-for-byte what it is today until the gate
       actually applies.
       ------------------------------------------------------------------ */
    function ensureOverlay() {
      if (guard) return guard;

      var parent = input.parentNode;
      if (!parent) return null;

      // Wrap so the transparent button can be positioned over the field.
      var wrap = document.createElement("div");
      wrap.className = "mo-logo-upload-wrap";
      parent.insertBefore(wrap, input);
      wrap.appendChild(input);

      guard = document.createElement("button");
      guard.type = "button";
      guard.className = "mo-logo-upload-overlay";
      guard.setAttribute("aria-label", copy("login_sign_in", "Sign in") + " to upload your logo");
      wrap.appendChild(guard);

      guard.addEventListener("click", function (evt) {
        evt.preventDefault();
        evt.stopPropagation();

        if (blocked()) {
          openModal();
          return;
        }

        // Allowed: hide the guard and open the real picker. This runs inside
        // the button's own activation, so it still counts as a user gesture
        // and the file dialog is permitted to open.
        guard.style.display = "none";
        input.click();
      });

      return guard;
    }

    function sync() {
      // Only cover the field while the gate would actually stop them.
      if (!blocked()) {
        if (guard) guard.style.display = "none";
        return;
      }
      var g = ensureOverlay();
      if (g) g.style.display = "";
    }

    /* ------------------------------------------------------------------
       SECOND LINE OF DEFENCE — do not remove.

       The overlay above depends on the injected .mo-logo-upload-wrap div
       getting a usable height from whatever theme CSS surrounds it. Across 15
       different section templates that is not safe to assume: a flex or grid
       parent (e.g. .pdp-col-9) can collapse an unstyled child to zero height,
       leaving the overlay present but covering nothing — the field then opens
       the picker as if no gate existed.

       These listeners sit on the INPUT itself in the CAPTURE phase, so they
       fire before anything else and cannot be defeated by layout at all.
       preventDefault() on a file input's click reliably suppresses the dialog.

       Unlike the overlay these ARE registered unconditionally, because they
       mutate nothing: each one returns immediately unless blocked(). They are
       also what covers the window before the settings response arrives, now
       that the overlay is built lazily.

       The overlay is kept because it is what preserves the user gesture on the
       allowed path (letting us open the picker immediately after the gate
       passes). This pair is belt and braces, not duplication.
       ------------------------------------------------------------------ */
    input.addEventListener(
      "click",
      function (evt) {
        if (!blocked()) return;
        evt.preventDefault();
        evt.stopPropagation();
        openModal();
      },
      true
    );

    // Keyboard activation can open the picker without ever firing a click.
    input.addEventListener(
      "keydown",
      function (evt) {
        if (!blocked()) return;
        if (evt.key === "Enter" || evt.key === " " || evt.key === "Spacebar") {
          evt.preventDefault();
          evt.stopPropagation();
          openModal();
        }
      },
      true
    );

    /* Last resort: if a file somehow gets selected while blocked — a drag and
       drop onto the field, or a browser quirk — discard it rather than upload.
       The server would reject it anyway, but this avoids a pointless round trip
       and a confusing error. */
    input.addEventListener(
      "change",
      function (evt) {
        if (!blocked()) return;
        evt.stopImmediatePropagation();
        input.value = "";
        openModal();
      },
      true
    );

    sync();
    // Re-evaluate once settings arrive.
    loadSettings().then(safe(sync));

    input.__moSync = sync;
  });

  /* --------------------------------------------------- post-login banner */

  var showReturnBanner = safe(function () {
    if (!CFG.loggedIn) return;
    if (session("get", INTENT_KEY) !== "1") return;
    session("del", INTENT_KEY);

    var input = document.querySelector(FILE_INPUT_SELECTOR);
    if (!input) return;

    var banner = document.createElement("div");
    banner.className = "mo-logo-upload-banner";
    banner.setAttribute("role", "status");
    // Browsers require a fresh user gesture to open a file dialog, so we
    // genuinely cannot re-open the picker for them. Say so plainly.
    banner.textContent = "You're signed in — choose your file to continue.";

    var wrap = input.closest(".mo-logo-upload-wrap") || input;
    if (wrap.parentNode) wrap.parentNode.insertBefore(banner, wrap);

    if (input.focus) input.focus();
  });

  /* ----------------------------------------------------------- public API */

  /**
   * Called by mo-logo-upload-image-js.liquid before it sends an upload.
   * Resolves true when the upload may proceed.
   *
   * Fail-open by design: anything unexpected resolves true and lets the SERVER
   * make the real decision.
   */
  function ensureAllowed() {
    return loadSettings()
      .then(function () {
        if (!blocked()) return true;
        openModal();
        return false;
      })
      .catch(function () {
        return true;
      });
  }

  /**
   * Is this file small enough to send?
   *
   * Vercel serverless functions reject any request body over ~4.5 MB at the
   * EDGE — before a single line of our code runs. That rejection carries no
   * CORS headers, so the browser reports it as an opaque CORS failure and the
   * shopper just sees "Upload failed". Checking here turns that into a clear,
   * actionable message and saves sending megabytes that can only be discarded.
   *
   * The ceiling comes from the settings endpoint so the theme and the app can
   * never disagree about it. Falls back to 4 MB if settings are unavailable —
   * a slightly strict guess is far better than a guaranteed 413.
   *
   * @returns {{ok: true} | {ok: false, message: string}}
   */
  function checkSize(file) {
    var maxMb = (settings && settings.maxUploadMb) || 4;
    var maxBytes = maxMb * 1024 * 1024;
    if (!file || typeof file.size !== "number" || file.size <= maxBytes) {
      return { ok: true };
    }
    var actual = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      message:
        "That file is " + actual + " MB. The maximum is " + maxMb +
        " MB — please compress it or save a smaller version and try again.",
    };
  }

  /**
   * Map a server error code to a shopper-facing modal.
   *
   * Only codes the shopper can ACT ON open the modal, because its one button is
   * "Sign in". Anything else returns false and the caller shows the server's
   * message in the status line instead.
   */
  var handleUploadError = safe(function (code, message) {
    if (code === "login_required" || code === "invalid_signature") {
      openModal(message || "", "error");
      return true;
    }
    return false;
  });

  window.MoLogoUpload = {
    ensureAllowed: ensureAllowed,
    checkSize: checkSize,
    handleUploadError: handleUploadError,
    openModal: openModal,
    closeModal: closeModal,
    /** Exposed so the upload snippet can attach the extra FormData fields. */
    identity: function () {
      return {
        shop: CFG.shop || "",
        customer_gid: CFG.customerGid || "",
        customer_email: CFG.customerEmail || "",
        customer_sig: CFG.sig || "",
        login_override: CFG.loginOverride || "default",
        /* ⚠️ DO NOT REMOVE. One of the six fields in the signed HMAC payload:
             v1|shop|login_override|verification_override|customer_gid|email
           Drop it and the server hashes a different string, so every upload
           fails with 401. Unrelated to whether email verification exists. */
        verification_override: CFG.verificationOverride || "default",
        product_id: (CFG.product && CFG.product.id) || "",
        product_handle: (CFG.product && CFG.product.handle) || "",
        product_url: (CFG.product && CFG.product.url) || "",
      };
    },
  };

  /* ------------------------------------------------------------- bootstrap */

  var init = safe(function () {
    if (!CFG.secretConfigured && window.console && console.warn) {
      console.warn(
        "[mo-logo-upload] MO_LOGO_UPLOAD_SECRET has not been replaced in " +
          "snippets/mo-logo-upload-config.liquid — gated uploads will fail."
      );
    }

    // Start fetching immediately so the answer is usually ready before the
    // shopper clicks. A click that arrives first is NOT blocked (fail open).
    loadSettings();

    var inputs = document.querySelectorAll(FILE_INPUT_SELECTOR);
    for (var i = 0; i < inputs.length; i++) attach(inputs[i]);

    showReturnBanner();

    var root = document.getElementById("mo-logo-upload-gate");
    if (root) {
      root.addEventListener("click", function (evt) {
        if (evt.target.closest("[data-mo-close]")) {
          evt.preventDefault();
          closeModal();
          return;
        }
        // Click on the dim overlay itself (not the card) closes it.
        if (evt.target.hasAttribute("data-mo-modal")) closeModal();
      });
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
